import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

const LoginForm: React.FC = () => {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({ email, password });
      navigate('/dashboard');
    } catch (err: any) {
      const responseDetail = err?.response?.data?.detail;
      if (responseDetail) {
        setError(responseDetail);
      } else if (err?.message?.toLowerCase?.().includes('network')) {
        setError('Cannot reach backend API. Ensure backend is running on http://localhost:8000.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignin = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      await loginWithGoogle();
      navigate('/dashboard');
    } catch (err: any) {
      const responseDetail = err?.response?.data?.detail;
      if (responseDetail) {
        setError(responseDetail);
      } else {
        setError(err?.message || 'Google sign-in failed. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={handleSubmit}>
        <h1>Welcome Back</h1>
        <p>Log in to access EcoPredict analytics.</p>

        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        <label htmlFor="password">Password</label>
        <div className="password-field">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={8}
          />
          <button
            type="button"
            className="password-visibility-toggle"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            onClick={() => setShowPassword((prev) => !prev)}
          >
            <span aria-hidden="true">{showPassword ? '🙈' : '👁'}</span>
            <span>{showPassword ? 'Hide' : 'Show'}</span>
          </button>
        </div>

        {error && <p className="form-error">{error}</p>}

        <button
          type="button"
          className="google-signin-btn"
          onClick={handleGoogleSignin}
          disabled={loading || googleLoading}
        >
          {googleLoading ? 'Connecting to Google...' : 'Continue with Google'}
        </button>

        <div className="auth-divider" aria-hidden="true">
          <span>or</span>
        </div>

        <button type="submit" disabled={loading || googleLoading}>
          {loading ? 'Logging in...' : 'Login'}
        </button>

        <p className="form-alt-action">
          New to EcoPredict? <Link to="/signup">Create an account</Link>
        </p>
      </form>
    </div>
  );
};

export default LoginForm;
